<template>
    <kiosk-team-details :teamId="props.teamId">
        <div class="flex flex-col w-full justify-center gap-8">
            <page-header>Single Sign-On</page-header>

            <page-loading v-if="loading" />

            <template v-else-if="sso">
                <vx-expandable-card>
                    <template v-slot:header>Require SSO</template>
                    <template v-slot:content>
                        <div class="flex items-center justify-between gap-4">
                            <div>
                                <vx-badge :color="sso.require_sso ? 'rose' : 'slate'" data-test="require-sso-status">
                                    {{ sso.require_sso ? 'On' : 'Off' }}
                                </vx-badge>
                                <span
                                    v-if="!sso.feature_enabled && sso.require_sso"
                                    class="ml-2 text-sm text-slate-500"
                                    data-test="feature-off"
                                >
                                    Feature flag is off; Require SSO is still enforced.
                                </span>
                            </div>
                            <vx-button
                                v-if="sso.require_sso"
                                color="danger"
                                @click="disableModal = true"
                                data-test="disable-require-sso"
                            >
                                Turn off Require SSO <font-awesome-icon widthAuto :icon="faUnlock" />
                            </vx-button>
                        </div>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card>
                    <template v-slot:header>Connection</template>
                    <template v-slot:content>
                        <div v-if="sso.connection === null" class="text-sm text-slate-500" data-test="no-connection">
                            No connection.
                        </div>
                        <dl v-else class="grid gap-3 text-sm sm:grid-cols-[180px_1fr]" data-test="connection">
                            <dt class="text-slate-500">Status</dt>
                            <dd>
                                <vx-badge :color="sso.connection.enabled ? 'success' : 'slate'">
                                    {{ sso.connection.enabled ? 'Enabled' : 'Disabled' }}
                                </vx-badge>
                            </dd>
                            <dt class="text-slate-500">Protocol</dt>
                            <dd data-test="protocol">
                                {{ sso.connection.protocol === 'saml2' ? 'SAML 2.0' : 'OpenID Connect' }}
                            </dd>
                            <template v-if="sso.connection.protocol === 'saml2'">
                                <dt class="text-slate-500">IdP entity ID</dt>
                                <dd class="break-all">{{ sso.connection.idp_entity_id }}</dd>
                                <dt class="text-slate-500">IdP login URL</dt>
                                <dd class="break-all">{{ sso.connection.idp_sso_url }}</dd>
                                <dt class="text-slate-500">NameID format</dt>
                                <dd>{{ sso.connection.name_id_format }}</dd>
                                <dt class="text-slate-500">Certificates</dt>
                                <dd>
                                    <div
                                        v-for="certificate in sso.connection.certificates"
                                        :key="certificate.fingerprint"
                                        data-test="certificate"
                                    >
                                        <code class="text-xs">{{ certificate.fingerprint }}</code>
                                        <span class="ml-2" :class="certificateClass(certificate)">
                                            {{ certificateSummary(certificate) }}
                                        </span>
                                    </div>
                                </dd>
                                <dt class="text-slate-500">Email source</dt>
                                <dd>
                                    {{
                                        sso.connection.email_source === 'name_id'
                                            ? 'NameID'
                                            : `Attribute ${sso.connection.email_attribute}`
                                    }}
                                </dd>
                                <dt class="text-slate-500">Name attributes</dt>
                                <dd>{{ sso.connection.name_attributes.join(', ') || '-' }}</dd>
                                <dt class="text-slate-500">IdP-initiated</dt>
                                <dd>{{ sso.connection.allow_idp_initiated ? 'Allowed' : 'Not allowed' }}</dd>
                            </template>
                            <template v-else>
                                <dt class="text-slate-500">Issuer</dt>
                                <dd class="break-all">{{ sso.connection.issuer }}</dd>
                                <dt class="text-slate-500">Client ID</dt>
                                <dd class="break-all">{{ sso.connection.client_id }}</dd>
                                <dt class="text-slate-500">Client secret</dt>
                                <dd data-test="client-secret">
                                    {{ sso.connection.client_secret_set ? 'Set' : 'Not set' }}
                                </dd>
                                <dt class="text-slate-500">Client authentication</dt>
                                <dd data-test="client-auth-method">
                                    {{
                                        sso.connection.client_auth_method === 'client_secret_basic'
                                            ? 'Authorization header'
                                            : 'Request body'
                                    }}
                                </dd>
                                <dt class="text-slate-500">Scopes</dt>
                                <dd class="break-all">{{ sso.connection.scopes.join(' ') }}</dd>
                                <dt class="text-slate-500">Email claim</dt>
                                <dd>{{ sso.connection.email_claim }}</dd>
                                <dt class="text-slate-500">Name claims</dt>
                                <dd>{{ sso.connection.name_claims.join(', ') || '-' }}</dd>
                            </template>
                            <dt class="text-slate-500">Session lifetime</dt>
                            <dd>{{ sso.connection.session_lifetime_minutes }} minutes</dd>
                            <dt class="text-slate-500">Last SSO login</dt>
                            <dd>
                                {{ sso.connection.last_login_at ? shortDateTime(sso.connection.last_login_at) : '-' }}
                            </dd>
                        </dl>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card>
                    <template v-slot:header>Provisioning (SCIM)</template>
                    <template v-slot:content>
                        <div v-if="sso.scim === null" class="text-sm text-slate-500" data-test="no-scim">
                            No connection.
                        </div>
                        <dl v-else class="grid gap-3 text-sm sm:grid-cols-[180px_1fr]" data-test="scim">
                            <dt class="text-slate-500">Token</dt>
                            <dd>
                                <template v-if="sso.scim.token === null">None</template>
                                <template v-else>
                                    <vx-badge
                                        :color="scimTokenStateColor[scimTokenState(sso.scim.token)]"
                                        data-test="scim-token-state"
                                    >
                                        {{ scimTokenStateLabel[scimTokenState(sso.scim.token)] }}
                                    </vx-badge>
                                    <span class="ml-2 text-slate-500">
                                        created {{ shortDateTime(sso.scim.token.created_at) }}, expires
                                        {{ shortDateTime(sso.scim.token.expires_at) }}
                                    </span>
                                </template>
                            </dd>
                            <dt class="text-slate-500">Last request</dt>
                            <dd data-test="scim-last-request">
                                {{
                                    sso.scim.token?.last_request_at
                                        ? shortDateTime(sso.scim.token.last_request_at)
                                        : '-'
                                }}
                            </dd>
                            <dt class="text-slate-500">Revocation channel</dt>
                            <dd data-test="scim-revocation-channel">
                                {{
                                    revocationChannelLive(sso.scim.revocation_channel_until)
                                        ? `Live until ${shortDateTime(sso.scim.revocation_channel_until)}`
                                        : 'Not live'
                                }}
                            </dd>
                            <dt class="text-slate-500">Provisioned users</dt>
                            <dd>{{ sso.scim.users_count }}</dd>
                            <dt class="text-slate-500">Last events</dt>
                            <dd>
                                <div v-if="!sso.scim.events.length" class="text-slate-500">None yet.</div>
                                <div
                                    v-for="event in sso.scim.events"
                                    :key="event.id"
                                    class="flex flex-wrap gap-x-1"
                                    data-test="scim-event"
                                >
                                    <span class="font-bold">{{ scimEventLabel(event.action) }}</span>
                                    <a v-if="event.user_id" :href="`/kiosk/users/${event.user_id}`">
                                        {{ event.user_name }}
                                    </a>
                                    <span v-else-if="event.user_name">{{ event.user_name }}</span>
                                    <span v-if="event.reason" class="text-rose-700">
                                        ({{ scimRefusalLabel(event.reason) }})
                                    </span>
                                    <span class="text-slate-500">{{ shortDateTime(event.created_at) }}</span>
                                    <code v-if="event.reference_id" class="text-xs">{{ event.reference_id }}</code>
                                </div>
                            </dd>
                        </dl>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card>
                    <template v-slot:header>Verified domains</template>
                    <template v-slot:content>
                        <div v-if="!sso.email_domains.length" class="text-sm text-slate-500" data-test="no-domains">
                            No domains.
                        </div>
                        <vx-table v-else class="xl:grid-cols-5" rowBreakpoint="xl">
                            <vx-table-row isHeader>
                                <div>Domain</div>
                                <div>Status</div>
                                <div>Last check</div>
                                <div>Last result</div>
                                <div></div>
                            </vx-table-row>
                            <vx-table-row v-for="domain in sso.email_domains" :key="domain.id" data-test="domain">
                                <vx-table-row-title>
                                    {{ domain.display_domain }}
                                    <div
                                        v-if="domain.other_teams.length"
                                        class="text-xs text-slate-500 font-normal mt-1"
                                        data-test="other-teams"
                                    >
                                        Also verified by
                                        <template v-for="(other, index) in domain.other_teams" :key="other.id">
                                            <a :href="`/kiosk/teams/${other.id}`">{{ other.name }}</a
                                            ><template v-if="index < domain.other_teams.length - 1">, </template>
                                        </template>
                                    </div>
                                </vx-table-row-title>
                                <vx-table-row-item>
                                    <template v-slot:label>Status:</template>
                                    <vx-badge
                                        size="xs"
                                        :color="domainStatusColor[domainStatusView(domain)]"
                                        data-test="domain-status"
                                    >
                                        {{ domainStatusLabel[domainStatusView(domain)] }}
                                    </vx-badge>
                                    <span
                                        v-if="domainStatusView(domain) === 'failing'"
                                        class="ml-2 text-xs text-rose-700"
                                    >
                                        {{ pluralized('day', graceDaysLeft(domain) ?? 0, true) }} left
                                    </span>
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label>Last check:</template>
                                    {{ domain.last_checked_at ? shortDateTime(domain.last_checked_at) : '-' }}
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label>Last result:</template>
                                    {{ domain.last_check_result ?? '-' }}
                                </vx-table-row-item>
                                <vx-table-row-buttons>
                                    <vx-button
                                        size="sm"
                                        color="muted-dark"
                                        :loading="verifying === domain.id"
                                        @click="reverify(domain)"
                                        data-test="reverify"
                                    >
                                        Re-verify now <font-awesome-icon widthAuto :icon="faArrowsRotate" />
                                    </vx-button>
                                </vx-table-row-buttons>
                            </vx-table-row>
                        </vx-table>
                    </template>
                </vx-expandable-card>

                <div>
                    <h2 class="text-2xl font-extrabold mb-4">Login attempts</h2>
                    <kiosk-team-sso-logins-table :teamId="props.teamId" />
                </div>

                <vx-expandable-card>
                    <template v-slot:header>Audit log</template>
                    <template v-slot:content>
                        <div v-if="!sso.audit_logs.length" class="text-sm text-slate-500" data-test="no-audit-logs">
                            No audit entries yet.
                        </div>
                        <div
                            v-for="entry in sso.audit_logs"
                            :key="entry.id"
                            class="text-sm py-2 border-b border-slate-100 last:border-0"
                            data-test="audit-log"
                        >
                            <span class="font-bold">{{ auditActionLabel[entry.action] }}</span>
                            by <a :href="`/kiosk/users/${entry.admin_id}`">{{ entry.admin_name }}</a>
                            <template v-if="entry.user_id !== null">
                                for
                                <a :href="`/kiosk/users/${entry.user_id}`" data-test="audit-user"
                                    >user {{ entry.user_id }}</a
                                >
                            </template>
                            {{ shortDateTime(entry.created_at) }}
                            <div v-if="entry.reason" class="text-slate-500">{{ entry.reason }}</div>
                        </div>
                    </template>
                </vx-expandable-card>
            </template>
        </div>

        <kiosk-team-disable-require-sso-modal
            v-model:visible="disableModal"
            :teamId="props.teamId"
            @disabled="onRequireSsoDisabled"
        />
    </kiosk-team-details>
</template>

<script lang="ts" setup>
import { faArrowsRotate, faUnlock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxExpandableCard,
    VxTable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { onMounted, ref } from 'vue';
import { pluralized } from '~/components/filters';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import {
    certificateState,
    certificateStateClass,
    domainStatusColor,
    domainStatusLabel,
    domainStatusView,
    graceDaysLeft,
    revocationChannelLive,
    scimEventLabel,
    scimRefusalLabel,
    scimTokenState,
    scimTokenStateColor,
    scimTokenStateLabel,
    type CertificateState,
} from '~/components/settings/teams/sso/ssoSettingsHelpers';
import { useToasted } from '~/composables/useToasted';
import { adminSsoClient } from '~/services';
import type {
    KioskSsoAuditAction,
    KioskSsoCertificate,
    KioskSsoEmailDomain,
    KioskTeamSso,
} from '~/services/adminSsoClient.types';
import { shortDate, shortDateTime } from '~/utils/date';
import { catchRequestError } from '~/utils/request';
import { useTeamDetails } from '../composables/useTeamDetails';
import KioskTeamDetails from '../details/KioskTeamDetails.vue';
import KioskTeamDisableRequireSsoModal from './KioskTeamDisableRequireSsoModal.vue';
import KioskTeamSsoLoginsTable from './KioskTeamSsoLoginsTable.vue';

const props = defineProps<{
    teamId: number;
}>();

const toasted = useToasted();
const { refreshTeam } = useTeamDetails();

const sso = ref<KioskTeamSso | null>(null);
const loading = ref(true);
const verifying = ref<number | null>(null);
const disableModal = ref(false);

const certificateStateLabel: Record<CertificateState, string> = {
    valid: 'Valid until',
    expiring: 'Expires',
    expired: 'Expired',
};

const certificateSummary = (certificate: KioskSsoCertificate): string =>
    `${certificate.subject} · ${certificateStateLabel[certificateState(certificate)]} ${shortDate(
        certificate.not_after
    )}`;

const certificateClass = (certificate: KioskSsoCertificate): string =>
    certificateStateClass[certificateState(certificate)];

const auditActionLabel: Record<KioskSsoAuditAction, string> = {
    require_sso_disabled: 'Require SSO turned off',
    identity_unlinked: 'SSO identity unlinked',
    scim_token_generated: 'SCIM token generated',
    scim_token_extended: 'SCIM token extended',
    scim_token_revoked: 'SCIM token revoked',
    connection_saved: 'Connection saved',
    connection_removed: 'Connection removed',
};

const loadSso = async () => {
    try {
        const { data } = await adminSsoClient.getTeamSso(props.teamId);
        sso.value = data;
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        loading.value = false;
    }
};

const reverify = async (domain: KioskSsoEmailDomain) => {
    verifying.value = domain.id;

    try {
        const { data } = await adminSsoClient.verifyEmailDomain(props.teamId, domain.id);

        if (sso.value !== null) {
            sso.value.email_domains = sso.value.email_domains.map((candidate) =>
                candidate.id === data.id ? { ...data, other_teams: candidate.other_teams } : candidate
            );
        }

        const found = data.last_check_result === 'found';
        toasted[found ? 'success' : 'error'](
            found ? `${data.display_domain} verified.` : `${data.display_domain} could not be verified.`
        );
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        verifying.value = null;
    }
};

// The sidebar and the Users tab read require_sso from the team, and the new audit row only shows after a re-fetch.
const onRequireSsoDisabled = async () => {
    await Promise.all([loadSso(), refreshTeam(props.teamId)]);
};

onMounted(() => {
    loadSso();
});
</script>
